import {
  ChangeEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import WaveSurfer from 'wavesurfer.js';
import {useQueryClient} from '@tanstack/react-query';
import Regions, {Region} from 'wavesurfer.js/dist/plugins/regions';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline';
import * as yup from 'yup';
import {
  Box,
  IconButton,
  Button,
  Typography,
  Stack,
  Skeleton,
  TextField,
  Tooltip,
} from '@mui/material';
import {DataGridPro} from '@mui/x-data-grid-pro';
import PauseIcon from '@mui/icons-material/Pause';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import {GridColDef} from '@mui/x-data-grid';
import IconPlay from '../../asset/icons/icon_play.svg';
import StopIcon from '@mui/icons-material/Stop';
import {FFmpeg} from '@ffmpeg/ffmpeg';
import {apiGet, apiPost} from '../../axiosConfig';
import {useStoreSelector} from '../../store/configstore';
import {useStationContext} from '../../components/models/stations/StationContext';
import {companyIdSelector} from '../../store/Slices/companySlice';
import DialogApiRequestStatus from '../../components/dialogs/DialogApiRequestStatus';
import {useNavigate} from 'react-router-dom';
import {useRqCheckClipNameUnique} from '../../react_query/music_genres/ListGenresGrouped';
import {UpdateRegions} from './WaveformFunctions';
import IconDelete from '../../asset/icons/icon_delete.svg';
import {getMediaTracksProPaginateApiPath} from '../../react_query/media_tracks/ListMediaTrackPaginatPro';

interface WaveSurferOptions {
  container: HTMLElement | string;
  barWidth: number;
  barRadius: number;
  cursorWidth: number;
  waveColor: string;
  progressColor: string;
  backend: 'WebAudio' | 'MediaElement';
  mediaType: 'audio' | 'video';
  height: number;
  cursorColor: string;
  responsive: boolean;
  hideScrollbar: boolean;
  partialRender: boolean;
  normalize: boolean;
  sampleRate: number;
}
export interface IWaveFormRegions {
  id: string;
  startTime: string;
  endTime: string;
  total_duration: string;
  clip_name?: string;
  isClipNameDuplicate?: boolean;
}

export const formWaveSurferOptions = (
  ref: HTMLElement | string,
): WaveSurferOptions => ({
  container: ref,
  barWidth: 3,
  barRadius: 3,
  cursorWidth: 3,
  waveColor: '#6666ff',
  progressColor: '#6666ff',
  backend: 'MediaElement',
  mediaType: 'audio',
  height: 120,
  cursorColor: 'black',
  responsive: true,
  hideScrollbar: true,
  partialRender: false,
  normalize: true,
  sampleRate: 44100, // CD quality - default is 8000 which destroys audio quality
});

const secondsToTime = (secs: number) => {
  const secondsGiven = Math.round(secs);
  let minutes = Math.floor(secondsGiven / 60) || 0;
  const seconds = secondsGiven - minutes * 60 || 0;
  let timeString = '';
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    timeString += `${hours < 10 ? '0' : ''}${hours}:`;
    minutes %= 60;
  }
  timeString += `${minutes < 10 ? '0' : ''}${minutes}:`;
  timeString += `${seconds < 10 ? '0' : ''}${seconds}`;
  return timeString;
};

const fileNameValidationSchema = yup.object().shape({
  clip_name: yup
    .string()
    .required('Title is required')
    .min(3, 'Title is too short')
    .max(50, 'Title is too long'),
});
interface ITrackProps {
  id: number;
  task_id: number;
  title: string;
  artist: string;
  url: string;
}
type Props = {
  data: ITrackProps;
  type: string; // pss, clip
  walkupType?: string;
  clipType?: string;
  clipResetTab?: () => void;
  handleCloseViewModal?: () => void;
};
export default function Waveform({
  data,
  type,
  walkupType,
  clipType,
  clipResetTab,
  handleCloseViewModal,
}: Props) {
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>();
  const cursorPositionRef = useRef<number | null>(null);
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const ffmpegLoadedRef = useRef<boolean>(false);

  const wavesurfer = useRef<WaveSurfer | null>(null);
  const [regions, setRegions] = useState<IWaveFormRegions[]>([]);
  const wsRegions = useRef<Regions | null>(null);
  const regionsRef = useRef(regions);
  const [originalAudioData, setOriginalAudioData] =
    useState<ArrayBuffer | null>(null);
  const [isWaveformReady, setIsWaveformReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [validation, setValidation] = useState(false);
  const companyId = useStoreSelector(companyIdSelector);
  const [apiState, setApiState] = useState({status: '', error: null});
  const {stations, setStations} = useStationContext();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [liveId, setLiveId] = useState('');
  const [isRegionPlaying, setIsRegionPlaying] = useState<string | null>(null);
  const navigate = useNavigate();

  // Initialize FFmpeg once on component mount
  useEffect(() => {
    const initFFmpeg = async () => {
      if (ffmpegRef.current && ffmpegLoadedRef.current) return;

      const ffmpeg = new FFmpeg();
      // Load FFmpeg with default settings (uses bundled WASM from node_modules)
      await ffmpeg.load();
      ffmpegRef.current = ffmpeg;
      ffmpegLoadedRef.current = true;
    };

    initFFmpeg().catch(error => {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize FFmpeg:', error);
    });

    return () => {
      if (ffmpegRef.current) {
        ffmpegRef.current.terminate();
        ffmpegRef.current = null;
        ffmpegLoadedRef.current = false;
      }
    };
  }, []);

  const {
    data: uniqueCheck,
    isError,
    isFetching,
  } = useRqCheckClipNameUnique(companyId, name, Boolean(name));

  const handleEdit = useCallback(() => {
    setApiState({status: '', error: null});
  }, []);
  const handlePlayPauseClick = () => {
    wavesurfer.current?.playPause();
    setIsPlaying(prevIsPlaying => !prevIsPlaying);
  };

  useEffect(() => {
    regionsRef.current = regions;
  }, [regions]);

  const regionValidation = useCallback(updatedRegions => {
    yup
      .array()
      .of(fileNameValidationSchema)
      .validate(updatedRegions)
      .then(() => {
        setValidation(true);
      })
      .catch(() => {
        setValidation(false);
      });
  }, []);

  const handleRegionCreated = useCallback(
    (region: Region) => {
      const regionObj = {
        id: region.id,
        total_duration: secondsToTime(region.end - region.start),
        startTime: secondsToTime(region.start),
        endTime: secondsToTime(region.end),
        clip_name: '',
      };
      setRegions([...regionsRef.current, {...regionObj}]);
      regionValidation([...regionsRef.current, {...regionObj}]);
    },
    [regionValidation],
  );

  const handleRegionUpdated = (region: Region) => {
    const regionId = region.id;
    const updatedRegion = [...regionsRef.current];
    const updatedRegions = updatedRegion.map(regions => {
      const regionObj = {
        id: region.id,
        total_duration: secondsToTime(region.end - region.start),
        startTime: secondsToTime(region.start),
        endTime: secondsToTime(region.end),
        clip_name: regions.clip_name,
      };
      if (regions.id === regionId) {
        return regionObj;
      } else {
        return regions;
      }
    });
    setRegions(updatedRegions);
    setIsRegionPlaying(null);
  };
  const handleRegionOut = () => {
    wavesurfer.current?.stop();
    setIsRegionPlaying(null);
  };
  const handleRegionClicked = (region: Region) => {
    region.play();
    setIsRegionPlaying(null);
  };

  useEffect(() => {
    if (data.url && waveformRef.current) {
      const options = formWaveSurferOptions(waveformRef.current);
      wavesurfer.current = WaveSurfer.create(options);
      // Load audio via proxy endpoint with auth
      apiGet('/v1/proxy-song', {
        params: {
          url: data.url,
        },
        responseType: 'arraybuffer',
        headers: {
          Accept: 'audio/mpeg',
        },
      })
        .then(response => {
          // Store original audio data for clip extraction
          setOriginalAudioData(response.data);
          const blob = new Blob([response.data], {type: 'audio/mpeg'});
          wavesurfer.current?.loadBlob(blob);
        })
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error('Error fetching or decoding audio:', error);
        });
      wsRegions.current = wavesurfer.current?.registerPlugin(Regions.create());
      wavesurfer.current?.registerPlugin(TimelinePlugin.create());
      wsRegions.current?.enableDragSelection({
        color: 'rgba(89, 19, 19, 0.37)',
      });
      wsRegions.current?.on('region-created', handleRegionCreated);
      wsRegions.current?.on('region-updated', handleRegionUpdated);
      wsRegions.current?.on('region-out', handleRegionOut);
      wsRegions.current?.on('region-clicked', handleRegionClicked);
      wavesurfer.current?.on('ready', () => {
        setIsWaveformReady(true);
      });
    } else {
      setRegions([]);
      setStations([]);
      wavesurfer.current = null;
    }
    return () => {
      // Cleanup event listeners when the component unmounts or dependencies change.
      wsRegions.current?.un('region-created', handleRegionCreated);
      wsRegions.current?.un('region-updated', handleRegionUpdated);
      wsRegions.current?.un('region-out', handleRegionOut);
      wsRegions.current?.un('region-clicked', handleRegionClicked);
      wavesurfer.current?.destroy();
      setRegions([]);
      setValidation(false);
      setIsWaveformReady(false);
    };
  }, [data.url, handleRegionCreated, setStations]);

  const handleStop = useCallback(() => {
    wavesurfer.current?.stop();
    setIsPlaying(false);
  }, []);

  const handlePlayPause = useCallback(
    (id: string, type = 'play') => {
      if (wavesurfer.current) {
        if (isRegionPlaying) {
          handleStop();
          setTimeout(() => {
            playRegion(id, type);
          }, 50);
          return;
        }
        playRegion(id, type);
      }
    },
    [isRegionPlaying, handleStop],
  );

  const playRegion = (id: string, type: string) => {
    const regiontoPlay = wsRegions.current
      ?.getRegions()
      .find((obj: Region) => obj.id === id);

    if (regiontoPlay) {
      if (type === 'play') {
        regiontoPlay.play();
        setIsRegionPlaying(id);
      } else {
        wavesurfer.current?.stop();
        setIsRegionPlaying(null);
      }
    }
  };

  const handleDelete = useCallback(
    (id: string) => {
      const regionslist = [...regionsRef.current];
      const updatedRegions: IWaveFormRegions[] = [];
      regionslist.forEach((regions: IWaveFormRegions) => {
        if (regions.id !== id) {
          updatedRegions.push(regions);
        }
      });
      const regiontoPlay = wsRegions.current
        ?.getRegions()
        .find((obj: Region) => obj.id === id);
      regiontoPlay?.remove();
      setRegions(updatedRegions);
      regionValidation(updatedRegions);
      setIsRegionPlaying(null);
    },
    [regionValidation],
  );

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const updatedRegions = UpdateRegions({
      regions: regionsRef.current,
      id: event.target.id,
      key: 'clip_name',
      value: event.target.value.trim().length !== 0 ? event.target.value : '',
    });
    setRegions(updatedRegions);
    cursorPositionRef.current = event.target.selectionStart;

    yup
      .array()
      .of(fileNameValidationSchema)
      .validate(updatedRegions)
      .then(() => {
        setLiveId(event.target.id);
        if (event.target.value.trim().length !== 0) {
          setName(event.target.value);
        }
        setValidation(true);
      })
      .catch(() => {
        setValidation(false);
      });
  };

  // Use layout effect to synchronously restore cursor position
  useLayoutEffect(() => {
    if (inputRef.current && cursorPositionRef.current !== null) {
      inputRef.current.setSelectionRange(
        cursorPositionRef.current,
        cursorPositionRef.current,
      );
    }
  });
  useEffect(() => {
    if (!isError && !isFetching && uniqueCheck) {
      const updatedRegions = UpdateRegions({
        regions: regionsRef.current,
        id: liveId,
        key: 'isClipNameDuplicate',
        value: uniqueCheck.name_exist,
      });
      setRegions(updatedRegions);
    }
  }, [isError, isFetching, liveId, uniqueCheck]);
  const isWalkupOrCustom =
    walkupType === 'walkupclip' || clipType === 'customClip';
  const mlClass = isWalkupOrCustom ? 'ml-12' : 'ml-15';
  const ml = isWalkupOrCustom ? '' : 'ml-15';
  let columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'startTime',
        headerName: 'Start Time',
        width: 100,
        headerClassName: 'text-15 mt-8',
        cellClassName: 'border-bottom-none',
      },
      {
        field: 'endTime',
        headerName: 'End Time',
        width: 100,
        headerClassName: 'text-15 mt-8 ml-15',
        cellClassName: 'ml-15 border-bottom-none',
      },
      {
        field: 'total_duration',
        headerName: 'Total Duration',
        width: 150,
        headerClassName: 'text-15 mt-8 ml-15',
        cellClassName: 'ml-15 border-bottom-none',
      },
      {
        field: 'clip_name',
        headerName: 'Clip Name',
        headerClassName: `text-15 mt-8 ${mlClass}`,
        cellClassName: `${mlClass} border-bottom-none`,
        width: 300,
        renderCell: params => (
          <TextField
            error={
              params.row.isClipNameDuplicate
                ? params.row.isClipNameDuplicate
                : false
            }
            value={params.row.clip_name}
            variant='outlined'
            id={String(params.id)}
            placeholder='3-50 characters Allowed'
            onChange={handleChange}
            className='text-wrap-helper'
            onSelect={e => {
              cursorPositionRef.current = (
                e.target as HTMLInputElement
              ).selectionStart;
            }}
            inputRef={inputRef}
            onKeyDown={e => {
              if (e.key === ' ') {
                e.stopPropagation();
              }
            }}
            helperText={
              params.row.isClipNameDuplicate &&
              params.row.isClipNameDuplicate === true
                ? 'This clip name is already taken. Please provide a unique clip name to proceed.'
                : null
            }
          />
        ),
      },
      {
        field: 'actions',
        sortable: false,
        headerName: 'Actions',
        width: 150,
        headerClassName: `${ml} text-15 mt-8`,
        cellClassName: `${ml} border-bottom-none`,
        renderCell: params => (
          <Box className='flex items-center'>
            {isRegionPlaying === params.id ? (
              <StopIcon
                onClick={() => handlePlayPause(String(params.id), 'pause')}
              />
            ) : (
              <img
                src={IconPlay}
                alt='Play'
                className='cursor-pointer '
                onClick={() => handlePlayPause(String(params.id), 'play')}
              />
            )}
            <IconButton color='error' className='ml-3'>
              <img
                src={IconDelete}
                alt='Delete'
                className='cursor-pointer'
                onClick={() => handleDelete(String(params.id))}
              />
            </IconButton>
          </Box>
        ),
      },
    ],
    [handleDelete, handlePlayPause, isRegionPlaying, mlClass, ml],
  );
  if (type === 'pss') {
    const updatedColumns = columns.filter(
      column => column.field !== 'clip_name',
    );
    columns = updatedColumns;
  }

  const enablePendingDialogClip = () => {
    setApiState({status: 'pending', error: null});
    setTimeout(handleClip, 5, true);
  };

  const handleClip = async () => {
    setApiState({status: 'pending', error: null});
    const availRegions = wsRegions.current?.getRegions();
    const ffmpeg = ffmpegRef.current;

    if (
      !availRegions ||
      availRegions.length === 0 ||
      !originalAudioData ||
      !ffmpeg ||
      !ffmpegLoadedRef.current
    ) {
      setApiState({status: 'error', error: null});
      return;
    }

    const formData = new FormData();
    formData.append('media_id', String(data.id));

    try {
      const sessionId = `${Date.now()}_${Math.random()
        .toString(36)
        .slice(2, 9)}`;
      const inputFile = `source_${sessionId}.mp3`;

      // Write the original audio file once
      await ffmpeg.writeFile(inputFile, new Uint8Array(originalAudioData));

      let successCount = 0;
      // Process clips sequentially
      for (let index = 0; index < availRegions.length; index++) {
        const region = availRegions[index];
        if (!region) continue;

        const foundRegion = regionsRef.current.find(
          (obj: IWaveFormRegions) => obj.id === region.id,
        );
        const duration = Math.round(region.end - region.start);
        const outputFile = `clip_${sessionId}_${index}.mp3`;

        try {
          // Extract clip with high quality re-encoding
          // eslint-disable-next-line no-await-in-loop
          await ffmpeg.exec([
            '-ss',
            region.start.toFixed(3),
            '-i',
            inputFile,
            '-t',
            (region.end - region.start).toFixed(3),
            '-b:a',
            '320k',
            '-y',
            outputFile,
          ]);

          // eslint-disable-next-line no-await-in-loop
          const outputData = await ffmpeg.readFile(outputFile);

          if (outputData && (outputData as Uint8Array).length > 0) {
            const clipBlob = new Blob(
              [new Uint8Array(outputData as Uint8Array)],
              {
                type: 'audio/mp3',
              },
            );
            const fileName = `${
              foundRegion?.clip_name?.trim() || `clip_${index}`
            }.mp3`;
            const file = new File([clipBlob], fileName, {type: 'audio/mp3'});
            formData.append(`files[${index}]`, file);
            formData.append(
              `names[${index}]`,
              foundRegion?.clip_name?.trim() || `clip_${index}`,
            );
            formData.append(`duration[${index}]`, `${duration}`);
            successCount++;
          }

          // Cleanup output file
          // eslint-disable-next-line no-await-in-loop
          await ffmpeg.deleteFile(outputFile);
        } catch (clipError) {
          // eslint-disable-next-line no-console
          console.error(`Failed to extract clip ${index}:`, clipError);
        }
      }

      // Cleanup input file
      await ffmpeg.deleteFile(inputFile);

      if (successCount === 0) {
        setApiState({status: 'error', error: null});
        return;
      }

      apiPost(
        `/v1/company/${companyId}/on-demand-playlist/create-clip`,
        formData,
      )
        .then(() => {
          setApiState({status: 'success', error: null});
          if (type === 'clip' && walkupType !== 'walkupClip') {
            navigate('/music/on-demand', {
              state: {
                setCustomClip: true,
                stationId: stations && stations[0] ? stations[0].id : 0,
              },
            });
            clipResetTab?.();
          } else if (type === 'clip' && walkupType === 'walkupClip') {
            queryClient.invalidateQueries({
              queryKey: [
                getMediaTracksProPaginateApiPath(companyId),
                {track_category: 4},
              ],
            });
          }
          handleCloseViewModal?.();
        })
        .catch(error => {
          setApiState({status: 'error', error});
        });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('FFmpeg processing error:', error);
      setApiState({status: 'error', error: null});
    }
  };

  const handleClose = () => {
    setApiState({status: '', error: null});
  };
  return (
    <Box style={{height: '100%', position: 'relative'}}>
      <div
        id='waveform'
        className='mt-5 black-variant1'
        ref={waveformRef}></div>
      {isWaveformReady ? (
        <Stack direction='row' sx={{justifyContent: 'space-between'}}>
          <Tooltip title={data.title}>
            <Typography className='text-center p-2 mt-10'>
              Title:{' '}
              {data.title.length > 25
                ? `${data.title.slice(0, 20)}...`
                : data.title}
            </Typography>
          </Tooltip>

          <Tooltip title={data.artist}>
            <Typography className='text-center p-2 mt-10'>
              Artist:{' '}
              {data.artist.length > 25
                ? `${data.artist.slice(0, 20)}...`
                : data.artist}
            </Typography>
          </Tooltip>
          <Typography className='text-center p-2 mt-10 ml-25'>
            Duration :{secondsToTime(wavesurfer.current?.getDuration() ?? 0)}
          </Typography>
          <Button
            onClick={handlePlayPauseClick}
            variant='contained'
            size='small'
            color='primary'
            className='mt-10 p-5'>
            {isPlaying ? (
              <PauseIcon />
            ) : (
              <img src={IconPlay} alt='Play' className='mr-3 w-auto h-auto' />
            )}
            {isPlaying ? 'Pause' : 'Play'}
          </Button>
          <Button
            onClick={handleStop}
            size='small'
            variant='contained'
            color='primary'
            className='mt-10 waveform-stop-button p-5 mr-5'>
            <StopCircleIcon className='mr-3 ' /> Stop
          </Button>
        </Stack>
      ) : (
        <>
          {data.url && (
            <Skeleton
              variant='rectangular'
              sx={{height: '30%', position: 'relative', bottom: '200px'}}
            />
          )}
        </>
      )}
      {regions.length > 0 && (
        <Box sx={{height: '40vh', width: '100%'}}>
          <DataGridPro
            rows={regions}
            rowHeight={140}
            columns={columns}
            disableColumnMenu
            checkboxSelection={type !== 'clip'}
            disableRowSelectionOnClick
          />
          {validation && stations && stations.length > 0 && (
            <Button
              sx={{
                left: '50%',
                transform: 'translateX(-50%)',
                bottom: 8,
                position: 'absolute',
              }}
              variant='outlined'
              onClick={enablePendingDialogClip}
              disabled={regions.some(val => val.isClipNameDuplicate === true)}>
              Save
            </Button>
          )}
        </Box>
      )}
      {apiState.status !== '' && (
        <DialogApiRequestStatus
          apiState={apiState}
          onRetry={handleClip}
          onEdit={handleEdit}
          onClose={handleClose}
        />
      )}
    </Box>
  );
}
